<template>
  <div v-if="visible" class="sim-overlay" @click.self="$emit('close')">
    <div class="sim-modal">

      <!-- ── Header ───────────────────────────────────────── -->
      <div class="sim-header">
        <div class="sim-header-left">
          <span class="sim-icon">▶</span>
          <div>
            <div class="sim-title">Flow Simulator</div>
            <div class="sim-subtitle">{{ flowName }}</div>
          </div>
        </div>
        <div class="sim-header-right">
          <button v-if="phase === 'chat' && stateHistory.length > 0" class="sim-undo-btn" :disabled="loading" @click="undoStep" title="Undo last step">← Back</button>
          <button v-if="phase === 'chat'" :class="['sim-debug-toggle-btn', activeView !== 'chat' && 'active']" @click="activeView = activeView === 'chat' ? 'debug' : 'chat'" title="Debug panel">🐛</button>
          <button v-if="phase === 'chat'" class="sim-restart-btn" @click="restart" title="Restart simulation">↺ Restart</button>
          <button class="sim-close-btn" @click="$emit('close')">✕</button>
        </div>
      </div>

      <!-- ── PHASE: Setup ─────────────────────────────────── -->
      <div v-if="phase === 'setup'" class="sim-setup">
        <div class="sim-setup-notice">
          <span class="sim-notice-icon">🔒</span>
          <span>Our database stays untouched — all writes are rolled back after each step. External APIs (AI, Google, HTTP) run for real.</span>
        </div>

        <div class="sim-setup-fields">
          <div class="sim-field">
            <label>Customer Phone</label>
            <input v-model="seed.customer_phone" type="text" placeholder="+201112936659" />
            <small>Used for country targeting &amp; advertiser logic</small>
          </div>
          <div class="sim-field">
            <label>Customer Name <span class="sim-optional">(optional)</span></label>
            <input v-model="seed.customer_name" type="text" placeholder="e.g. John" />
          </div>

          <div class="sim-extra-vars">
            <div class="sim-extra-vars-header">
              <span>Pre-set Variables <span class="sim-optional">(optional)</span></span>
              <button type="button" class="sim-add-var-btn" @click="addExtraVar">+ Add</button>
            </div>
            <small class="sim-extra-hint">Only needed if your flow expects variables set by an external system before it starts.</small>
            <div v-for="(ev, idx) in extraVars" :key="idx" class="sim-extra-var-row">
              <input v-model="ev.key" type="text" class="sim-extra-key" placeholder="variable_name" />
              <span class="sim-extra-eq">=</span>
              <input v-model="ev.value" type="text" class="sim-extra-val" placeholder="value" />
              <button type="button" class="sim-remove-var-btn" @click="extraVars.splice(idx, 1)">✕</button>
            </div>
          </div>
        </div>

        <button class="sim-start-btn" :disabled="starting" @click="startSimulation">
          {{ starting ? 'Starting…' : '▶  Start Simulation' }}
        </button>
      </div>

      <!-- ── PHASE: Chat ──────────────────────────────────── -->
      <div v-if="phase === 'chat'" class="sim-phone">

        <!-- Status bar -->
        <div class="sim-status-bar">
          <span class="sim-contact-name">Flow Bot</span>
          <span class="sim-contact-status">{{ flowEnded ? 'Flow ended' : 'Active' }}</span>
        </div>

        <!-- View tabs -->
        <div class="sim-view-tabs">
          <button :class="['sim-view-tab', activeView === 'chat' && 'active']" @click="activeView = 'chat'">💬 Chat</button>
          <button :class="['sim-view-tab', activeView === 'debug' && 'active']" @click="activeView = 'debug'">🐛 Debug</button>
          <button :class="['sim-view-tab', activeView === 'session' && 'active']" @click="activeView = 'session'">📋 Session</button>
        </div>

        <!-- Messages area -->
        <div ref="msgArea" class="sim-messages" v-show="activeView === 'chat'">
          <div v-if="messages.length === 0 && loading" class="sim-typing">
            <span></span><span></span><span></span>
          </div>

          <template v-for="(msg, idx) in messages" :key="idx">

            <!-- Bot text bubble -->
            <div v-if="msg.from === 'bot' && msg.type === 'text'" class="sim-bubble sim-bubble-bot">
              <div class="sim-bubble-body">{{ msg.body }}</div>
              <div class="sim-bubble-time">{{ msg.time }}</div>
            </div>

            <!-- Bot image bubble -->
            <div v-else-if="msg.from === 'bot' && msg.type === 'image'" class="sim-bubble sim-bubble-bot sim-bubble-media">
              <img :src="msg.url" class="sim-img" :alt="msg.caption || 'image'" @error="$event.target.style.display='none'" />
              <div v-if="msg.caption" class="sim-bubble-body sim-caption">{{ msg.caption }}</div>
              <div class="sim-bubble-time">{{ msg.time }}</div>
            </div>

            <!-- Bot buttons bubble -->
            <div v-else-if="msg.from === 'bot' && msg.type === 'buttons'" class="sim-bubble sim-bubble-bot sim-bubble-interactive">
              <div v-if="msg.header" class="sim-msg-header">{{ msg.header }}</div>
              <div class="sim-bubble-body">{{ msg.body }}</div>
              <div v-if="msg.footer" class="sim-msg-footer">{{ msg.footer }}</div>
              <div class="sim-btn-list">
                <button
                  v-for="btn in msg.buttons"
                  :key="btn.reply.id"
                  class="sim-reply-btn"
                  :disabled="!isLastInteractive(idx) || loading || flowEnded"
                  @click="sendReply(btn.reply.id, btn.reply.title)"
                >{{ btn.reply.title }}</button>
              </div>
              <div class="sim-bubble-time">{{ msg.time }}</div>
            </div>

            <!-- Bot list bubble -->
            <div v-else-if="msg.from === 'bot' && msg.type === 'list'" class="sim-bubble sim-bubble-bot sim-bubble-interactive">
              <div v-if="msg.header" class="sim-msg-header">{{ msg.header }}</div>
              <div class="sim-bubble-body">{{ msg.body }}</div>
              <div class="sim-list-toggle">
                <button
                  class="sim-list-open-btn"
                  :disabled="!isLastInteractive(idx) || loading || flowEnded"
                  @click="toggleList(idx)"
                >
                  {{ msg.listOpen ? '▲ Close' : `☰ ${msg.button_text || 'Select'}` }}
                </button>
              </div>
              <div v-if="msg.listOpen" class="sim-list-rows">
                <div v-for="section in msg.sections" :key="section.title" class="sim-list-section">
                  <div v-if="section.title" class="sim-list-section-title">{{ section.title }}</div>
                  <button
                    v-for="row in section.rows"
                    :key="row.id"
                    class="sim-list-row"
                    :disabled="loading || flowEnded"
                    @click="sendReply(row.id, row.title)"
                  >
                    <span class="sim-list-row-title">{{ row.title }}</span>
                    <span v-if="row.description" class="sim-list-row-desc">{{ row.description }}</span>
                  </button>
                </div>
              </div>
              <div class="sim-bubble-time">{{ msg.time }}</div>
            </div>

            <!-- System message -->
            <div v-else-if="msg.type === 'system'" class="sim-system-msg">
              {{ msg.body }}
            </div>

            <!-- User bubble -->
            <div v-else-if="msg.from === 'user'" class="sim-bubble sim-bubble-user">
              <div class="sim-bubble-body">{{ msg.body }}</div>
              <div class="sim-bubble-time">{{ msg.time }} ✓✓</div>
            </div>

          </template>

          <!-- Typing indicator -->
          <div v-if="loading && messages.length > 0" class="sim-typing">
            <span></span><span></span><span></span>
          </div>

          <!-- Flow ended banner -->
          <div v-if="flowEnded" class="sim-flow-ended">
            ✅ Flow completed
            <button class="sim-restart-inline" @click="restart">↺ Restart</button>
          </div>
        </div>

        <!-- Debug log panel -->
        <div v-show="activeView === 'debug'" class="sim-debug-panel">
          <div v-if="!debugLog.length" class="sim-debug-empty">No events yet — start and step through the flow.</div>
          <template v-for="(entry, i) in debugLog" :key="i">
            <div class="sim-dbg-step-header" v-if="i === 0 || entry.step !== debugLog[i-1].step">
              Step {{ entry.step }}
            </div>
            <div :class="['sim-dbg-entry', 'dbg-' + entry.event]">
              <span :class="['sim-dbg-badge', 'badge-' + entry.event]">{{ entry.event }}</span>
              <span class="sim-dbg-text">{{ formatDebugEntry(entry) }}</span>
            </div>
          </template>
        </div>

        <!-- Session variables panel -->
        <div v-show="activeView === 'session'" class="sim-debug-panel">
          <div v-if="!Object.keys(filteredSessionData).length" class="sim-debug-empty">No variables yet — start the simulation.</div>
          <div v-for="(val, key) in filteredSessionData" :key="key" class="sim-session-var">
            <span class="sim-session-key">{{ key }}</span>
            <span class="sim-session-val">{{ formatVarValue(val) }}</span>
          </div>
        </div>

        <!-- Input area -->
        <div class="sim-input-area" v-if="!flowEnded && activeView === 'chat'">

          <!-- Date input -->
          <template v-if="waitingFor && waitingFor.type === 'date'">
            <input ref="inputEl" v-model="userInput" type="date" class="sim-text-input" />
            <button class="sim-send-btn" :disabled="!userInput || loading" @click="sendText">Send</button>
          </template>

          <!-- Time input -->
          <template v-else-if="waitingFor && waitingFor.type === 'time'">
            <input ref="inputEl" v-model="userInput" type="time" class="sim-text-input" />
            <button class="sim-send-btn" :disabled="!userInput || loading" @click="sendText">Send</button>
          </template>

          <!-- Location mock -->
          <template v-else-if="waitingFor && waitingFor.type === 'location'">
            <button class="sim-location-btn" :disabled="loading" @click="sendMockLocation">
              📍 Send Mock Location (Cairo, Egypt)
            </button>
          </template>

          <!-- Media mock -->
          <template v-else-if="waitingFor && waitingFor.type === 'media'">
            <button class="sim-location-btn" :disabled="loading" @click="sendMockMedia">
              📎 Send Mock File
            </button>
          </template>

          <!-- Buttons / list: no text input, just tap the options above -->
          <template v-else-if="waitingFor && (waitingFor.type === 'buttons' || waitingFor.type === 'list')">
            <div class="sim-tap-hint">👆 Tap an option above</div>
          </template>

          <!-- Text / number / email -->
          <template v-else>
            <input
              ref="inputEl"
              v-model="userInput"
              :type="waitingFor ? waitingFor.type : 'text'"
              class="sim-text-input"
              :placeholder="waitingFor ? waitingFor.label : 'Type a message…'"
              :disabled="loading"
              @keyup.enter="sendText"
            />
            <button class="sim-send-btn" :disabled="!userInput.trim() || loading" @click="sendText">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
            </button>
          </template>

        </div>

      </div>
      <!-- /sim-phone -->

    </div>
  </div>
</template>

<script>
import DashboardService from '../services/dashboard'

export default {
  name: 'FlowSimulatorModal',

  props: {
    visible: { type: Boolean, default: false },
    flowId: { type: [Number, String], default: null },
    flowName: { type: String, default: 'Flow' },
    flowDefinition: { type: Object, default: () => ({}) }
  },

  emits: ['close'],

  data () {
    return {
      phase: 'setup', // 'setup' | 'chat'
      seed: {
        customer_phone: '',
        customer_name: ''
      },
      extraVars: [], // [{ key, value }] — manually added pre-seed variables
      starting: false,
      simSessionId: null,
      messages: [],
      waitingFor: null,
      flowEnded: false,
      loading: false,
      userInput: '',
      // Debug & undo
      debugLog: [],
      activeView: 'chat', // 'chat' | 'debug' | 'session'
      sessionData: {},
      stateHistory: [],
      stepCounter: 0
    }
  },

  computed: {
    filteredSessionData () {
      return Object.fromEntries(
        Object.entries(this.sessionData).filter(([k]) => !k.startsWith('__'))
      )
    }
  },

  watch: {
    visible (val) {
      if (val) this.reset()
    }
  },

  methods: {
    reset () {
      this.phase = 'setup'
      this.seed = { customer_phone: '', customer_name: '' }
      this.extraVars = []
      this.simSessionId = null
      this.messages = []
      this.waitingFor = null
      this.flowEnded = false
      this.loading = false
      this.userInput = ''
      this.debugLog = []
      this.activeView = 'chat'
      this.sessionData = {}
      this.stateHistory = []
      this.stepCounter = 0
    },

    addExtraVar () {
      this.extraVars.push({ key: '', value: '' })
    },

    restart () {
      this.reset()
    },

    now () {
      return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },

    async startSimulation () {
      if (!this.flowId) {
        alert('Please save the flow first.')
        return
      }
      this.starting = true
      try {
        const extra = {}
        for (const ev of this.extraVars) {
          if (ev.key.trim()) extra[ev.key.trim()] = ev.value
        }
        const result = await DashboardService.simulateFlowStart(this.flowId, { ...this.seed, ...extra })
        if (result.error) throw new Error(result.error)
        this.simSessionId = result.sim_session_id
        this.phase = 'chat'
        this.applyResult(result)
      } catch (err) {
        console.error('[Simulator] start error:', err)
        alert('Failed to start simulation: ' + err.message)
      } finally {
        this.starting = false
      }
    },

    applyResult (result) {
      this.stepCounter++
      const time = this.now()
      for (const msg of (result.messages || [])) {
        this.messages.push({ from: 'bot', time, ...msg })
      }
      this.waitingFor = result.waiting_for || null
      this.flowEnded = result.flow_ended || false
      for (const entry of (result.debug_log || [])) {
        this.debugLog.push({ ...entry, step: this.stepCounter })
      }
      if (result.session_data) {
        this.sessionData = result.session_data
      }
      this.$nextTick(() => this.scrollToBottom())
    },

    async sendText () {
      const text = this.userInput.trim()
      if (!text || this.loading) return
      this.userInput = ''
      this.messages.push({ from: 'user', type: 'text', body: text, time: this.now() })
      this.scrollToBottom()
      await this.step(text, 'text')
    },

    async sendReply (id, title) {
      if (this.loading || this.flowEnded) return
      // Close any open list
      this.messages.forEach(m => { m.listOpen = false })
      this.messages.push({ from: 'user', type: 'text', body: title, time: this.now() })
      this.scrollToBottom()
      await this.step(id, 'reply')
    },

    async sendMockLocation () {
      const mockText = 'location:30.044420,31.235712,Cairo Egypt'
      this.messages.push({ from: 'user', type: 'text', body: '📍 Cairo, Egypt (mock)', time: this.now() })
      this.scrollToBottom()
      await this.step(mockText, 'text')
    },

    async sendMockMedia () {
      this.messages.push({ from: 'user', type: 'text', body: '📎 mock-file.pdf (simulated)', time: this.now() })
      this.scrollToBottom()
      await this.step('mock_media_url', 'text')
    },

    toggleList (idx) {
      const msg = this.messages[idx]
      if (msg) msg.listOpen = !msg.listOpen
    },

    isLastInteractive (idx) {
      for (let i = this.messages.length - 1; i >= 0; i--) {
        if (['buttons', 'list'].includes(this.messages[i].type) && this.messages[i].from === 'bot') {
          return i === idx
        }
      }
      return false
    },

    async step (message, messageType) {
      // Snapshot state before this step so undo can restore it
      const snapshot = {
        messages: JSON.parse(JSON.stringify(this.messages)),
        waitingFor: this.waitingFor,
        debugLogLength: this.debugLog.length,
        sessionData: JSON.parse(JSON.stringify(this.sessionData)),
        flowEnded: this.flowEnded,
        stepCounter: this.stepCounter
      }
      this.loading = true
      try {
        const result = await DashboardService.simulateFlowStep(this.simSessionId, message, messageType)
        if (result.error) {
          this.messages.push({ type: 'system', body: '⚠️ ' + result.error, time: this.now() })
          return
        }
        this.stateHistory.push(snapshot)
        this.applyResult(result)
      } catch (err) {
        console.error('[Simulator] step error:', err)
        this.messages.push({ type: 'system', body: '⚠️ Network error: ' + err.message, time: this.now() })
      } finally {
        this.loading = false
        this.$nextTick(() => this.scrollToBottom())
      }
    },

    async undoStep () {
      if (!this.stateHistory.length || this.loading) return
      this.loading = true
      try {
        const result = await DashboardService.simulateFlowUndo(this.simSessionId)
        if (result.error) {
          this.messages.push({ type: 'system', body: '⚠️ ' + result.error, time: this.now() })
          return
        }
        const snapshot = this.stateHistory.pop()
        this.messages = snapshot.messages
        this.waitingFor = snapshot.waitingFor
        this.debugLog.splice(snapshot.debugLogLength)
        this.sessionData = snapshot.sessionData
        this.flowEnded = snapshot.flowEnded
        this.stepCounter = snapshot.stepCounter
        this.$nextTick(() => this.scrollToBottom())
      } catch (err) {
        this.messages.push({ type: 'system', body: '⚠️ Undo failed: ' + err.message, time: this.now() })
      } finally {
        this.loading = false
      }
    },

    formatDebugEntry (entry) {
      if (entry.event === 'node') {
        const name = entry.name ? ` "${entry.name}"` : ''
        return `${entry.type}${name}`
      }
      if (entry.event === 'api') {
        const ok = entry.success ? '✓' : '✗'
        const ms = entry.ms ? ` ${entry.ms}ms` : ''
        return `${ok} ${entry.method} ${entry.url} → ${entry.status}${ms}`
      }
      if (entry.event === 'error') {
        return entry.message || 'Unknown error'
      }
      return JSON.stringify(entry).slice(0, 120)
    },

    formatVarValue (val) {
      if (val === null || val === undefined) return '—'
      if (typeof val === 'object') return JSON.stringify(val).slice(0, 150)
      return String(val).slice(0, 200)
    },

    scrollToBottom () {
      const el = this.$refs.msgArea
      if (el) el.scrollTop = el.scrollHeight
    }
  }
}
</script>

<style scoped>
/* ── Overlay & Modal ───────────────────────────────────────────────── */
.sim-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sim-modal {
  width: 420px;
  max-width: 96vw;
  max-height: 92vh;
  background: var(--bg-primary, #fff);
  border-radius: 18px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.35);
}

/* ── Header ────────────────────────────────────────────────────────── */
.sim-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: #128C7E;
  color: #fff;
  flex-shrink: 0;
}

.sim-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.sim-icon {
  font-size: 20px;
}

.sim-title {
  font-size: 15px;
  font-weight: 700;
}

.sim-subtitle {
  font-size: 12px;
  opacity: 0.8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 220px;
}

.sim-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sim-restart-btn {
  background: rgba(255, 255, 255, 0.15);
  border: none;
  color: #fff;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  cursor: pointer;
}

.sim-restart-btn:hover {
  background: rgba(255, 255, 255, 0.28);
}

.sim-close-btn {
  background: none;
  border: none;
  color: #fff;
  font-size: 18px;
  cursor: pointer;
  line-height: 1;
  opacity: 0.8;
}

.sim-close-btn:hover { opacity: 1; }

/* ── Setup Screen ──────────────────────────────────────────────────── */
.sim-setup {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.sim-setup-notice {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  background: rgba(18, 140, 126, 0.08);
  border: 1px solid rgba(18, 140, 126, 0.25);
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 12px;
  color: var(--text-secondary);
  line-height: 1.5;
  margin-bottom: 18px;
}

.sim-notice-icon { font-size: 16px; flex-shrink: 0; }

.sim-setup-fields {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 20px;
}

.sim-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sim-field label {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
}

.sim-optional {
  font-weight: 400;
  opacity: 0.6;
}

.sim-field input {
  padding: 8px 10px;
  border: 1px solid var(--border-color, #ddd);
  border-radius: 8px;
  font-size: 13px;
  background: var(--bg-secondary, #f8f8f8);
  color: var(--text-primary);
}

.sim-field input:focus {
  outline: none;
  border-color: #128C7E;
}

.sim-field small {
  font-size: 11px;
  color: var(--text-muted, #999);
}

.sim-extra-vars {
  border: 1px solid var(--border-color, #e5e5e5);
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sim-extra-vars-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sim-extra-vars-header span {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
}

.sim-extra-hint {
  font-size: 11px;
  color: var(--text-muted, #999);
  line-height: 1.4;
}

.sim-add-var-btn {
  padding: 3px 10px;
  font-size: 12px;
  border: 1px solid #128C7E;
  border-radius: 6px;
  background: transparent;
  color: #128C7E;
  cursor: pointer;
}

.sim-add-var-btn:hover { background: rgba(18, 140, 126, 0.08); }

.sim-extra-var-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.sim-extra-key {
  flex: 2;
  padding: 6px 8px;
  border: 1px solid var(--border-color, #ddd);
  border-radius: 6px;
  font-size: 12px;
  background: var(--bg-secondary, #f8f8f8);
  color: var(--text-primary);
  font-family: monospace;
}

.sim-extra-val {
  flex: 3;
  padding: 6px 8px;
  border: 1px solid var(--border-color, #ddd);
  border-radius: 6px;
  font-size: 12px;
  background: var(--bg-secondary, #f8f8f8);
  color: var(--text-primary);
}

.sim-extra-key:focus, .sim-extra-val:focus {
  outline: none;
  border-color: #128C7E;
}

.sim-extra-eq {
  font-size: 13px;
  color: var(--text-muted, #999);
  flex-shrink: 0;
}

.sim-remove-var-btn {
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  font-size: 13px;
  padding: 2px 4px;
  flex-shrink: 0;
}

.sim-remove-var-btn:hover { color: #dc2626; }

.sim-detected-vars {
  background: rgba(249, 115, 22, 0.06);
  border: 1px solid rgba(249, 115, 22, 0.2);
  border-radius: 10px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sim-detected-label {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sim-detected-label span {
  font-size: 12px;
  font-weight: 600;
  color: #ea580c;
}

.sim-detected-label small {
  font-size: 11px;
  color: var(--text-muted, #999);
}

.sim-start-btn {
  width: 100%;
  padding: 12px;
  background: #128C7E;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}

.sim-start-btn:hover:not(:disabled) { background: #0f7267; }
.sim-start-btn:disabled { opacity: 0.6; cursor: not-allowed; }

/* ── Phone / Chat ──────────────────────────────────────────────────── */
.sim-phone {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  background: #ECE5DD;
}

/* WhatsApp-style wallpaper pattern */
.sim-phone::before {
  content: '';
  position: absolute;
  inset: 0;
  opacity: 0.04;
  pointer-events: none;
}

.sim-status-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 14px;
  background: #075E54;
  color: #fff;
  font-size: 12px;
  flex-shrink: 0;
}

.sim-contact-name { font-weight: 600; font-size: 14px; }
.sim-contact-status { font-size: 11px; opacity: 0.75; }

/* ── Messages ──────────────────────────────────────────────────────── */
.sim-messages {
  flex: 1;
  overflow-y: auto;
  padding: 12px 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: #ECE5DD;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.sim-bubble {
  max-width: 82%;
  border-radius: 12px;
  padding: 7px 10px 5px;
  position: relative;
  font-size: 13.5px;
  line-height: 1.4;
  word-break: break-word;
}

.sim-bubble-bot {
  align-self: flex-start;
  background: #fff;
  border-radius: 0 12px 12px 12px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.12);
  margin-bottom: 2px;
}

.sim-bubble-user {
  align-self: flex-end;
  background: #DCF8C6;
  border-radius: 12px 0 12px 12px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.12);
  margin-bottom: 2px;
}

.sim-bubble-body {
  color: #111;
  white-space: pre-wrap;
}

.sim-bubble-time {
  font-size: 10px;
  color: #999;
  text-align: right;
  margin-top: 3px;
}

/* Media bubble */
.sim-bubble-media { padding: 5px; }
.sim-img { width: 100%; max-width: 260px; border-radius: 8px; display: block; }
.sim-caption { padding: 4px 5px 0; font-size: 13px; color: #333; }

/* Bubble header/footer */
.sim-msg-header {
  font-size: 14px;
  font-weight: 700;
  color: #111;
  margin-bottom: 4px;
}

.sim-msg-footer {
  font-size: 11px;
  color: #888;
  margin-top: 4px;
}

/* Interactive bubbles — buttons */
.sim-bubble-interactive { padding-bottom: 8px; }

.sim-btn-list {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-top: 8px;
  border-top: 1px solid #eee;
  padding-top: 8px;
}

.sim-reply-btn {
  background: #fff;
  border: 1px solid #128C7E;
  color: #128C7E;
  border-radius: 8px;
  padding: 7px 12px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  text-align: center;
}

.sim-reply-btn:hover:not(:disabled) {
  background: #128C7E;
  color: #fff;
}

.sim-reply-btn:disabled { opacity: 0.45; cursor: not-allowed; }

/* List */
.sim-list-toggle {
  margin-top: 8px;
  border-top: 1px solid #eee;
  padding-top: 8px;
}

.sim-list-open-btn {
  width: 100%;
  background: none;
  border: 1px solid #128C7E;
  color: #128C7E;
  border-radius: 8px;
  padding: 7px;
  font-size: 13px;
  cursor: pointer;
}

.sim-list-open-btn:hover:not(:disabled) { background: rgba(18,140,126,0.08); }
.sim-list-open-btn:disabled { opacity: 0.45; cursor: not-allowed; }

.sim-list-rows {
  margin-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.sim-list-section-title {
  font-size: 11px;
  font-weight: 700;
  color: #666;
  padding: 4px 6px 2px;
  text-transform: uppercase;
}

.sim-list-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background: #f7f7f7;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 8px 10px;
  cursor: pointer;
  text-align: left;
  width: 100%;
  transition: background 0.12s;
}

.sim-list-row:hover:not(:disabled) { background: rgba(18,140,126,0.09); }
.sim-list-row:disabled { opacity: 0.45; cursor: not-allowed; }

.sim-list-row-title { font-size: 13px; font-weight: 500; color: #111; }
.sim-list-row-desc { font-size: 11px; color: #888; margin-top: 2px; }

/* System message */
.sim-system-msg {
  align-self: center;
  background: rgba(0,0,0,0.07);
  border-radius: 12px;
  padding: 5px 12px;
  font-size: 11.5px;
  color: #555;
  margin: 4px 0;
}

/* Typing indicator */
.sim-typing {
  align-self: flex-start;
  background: #fff;
  border-radius: 0 12px 12px 12px;
  padding: 10px 14px;
  display: flex;
  gap: 5px;
  align-items: center;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}

.sim-typing span {
  width: 8px;
  height: 8px;
  background: #b0b0b0;
  border-radius: 50%;
  display: block;
  animation: simBounce 1.2s infinite ease-in-out;
}
.sim-typing span:nth-child(2) { animation-delay: 0.2s; }
.sim-typing span:nth-child(3) { animation-delay: 0.4s; }

@keyframes simBounce {
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-6px); }
}

/* Flow ended */
.sim-flow-ended {
  align-self: center;
  background: rgba(18, 140, 126, 0.12);
  border: 1px solid rgba(18, 140, 126, 0.3);
  color: #075E54;
  border-radius: 12px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 6px;
}

.sim-restart-inline {
  background: none;
  border: 1px solid #075E54;
  color: #075E54;
  border-radius: 8px;
  padding: 2px 8px;
  font-size: 12px;
  cursor: pointer;
}

/* ── Input Area ────────────────────────────────────────────────────── */
.sim-input-area {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #F0F0F0;
  border-top: 1px solid #ddd;
  flex-shrink: 0;
}

.sim-text-input {
  flex: 1;
  padding: 9px 14px;
  border: none;
  border-radius: 22px;
  background: #fff;
  font-size: 13.5px;
  color: #111;
  outline: none;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.sim-send-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: #128C7E;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;
}

.sim-send-btn:hover:not(:disabled) { background: #0f7267; }
.sim-send-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.sim-location-btn {
  flex: 1;
  padding: 9px 14px;
  border: none;
  border-radius: 22px;
  background: rgba(18, 140, 126, 0.12);
  color: #128C7E;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-align: center;
}

.sim-location-btn:hover:not(:disabled) { background: rgba(18, 140, 126, 0.22); }
.sim-location-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.sim-tap-hint {
  flex: 1;
  text-align: center;
  font-size: 12px;
  color: #888;
  padding: 10px;
}

/* ── Undo & Debug Toggle Buttons ───────────────────────────────────── */
.sim-undo-btn {
  background: rgba(255, 255, 255, 0.15);
  border: none;
  color: #fff;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
}
.sim-undo-btn:hover:not(:disabled) { background: rgba(255, 255, 255, 0.28); }
.sim-undo-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.sim-debug-toggle-btn {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: rgba(255, 255, 255, 0.7);
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 14px;
  cursor: pointer;
  line-height: 1;
}
.sim-debug-toggle-btn.active { background: rgba(255, 200, 0, 0.25); color: #fbbf24; }
.sim-debug-toggle-btn:hover { color: #fff; }

/* ── View Tabs ─────────────────────────────────────────────────────── */
.sim-view-tabs {
  display: flex;
  background: #075E54;
  flex-shrink: 0;
}
.sim-view-tab {
  flex: 1;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  color: rgba(255, 255, 255, 0.6);
  font-size: 11.5px;
  font-weight: 600;
  padding: 6px 4px;
  cursor: pointer;
  transition: all 0.15s;
}
.sim-view-tab.active { color: #fff; border-bottom-color: #25D366; }
.sim-view-tab:hover:not(.active) { color: rgba(255, 255, 255, 0.85); }

/* ── Debug / Session Panels ────────────────────────────────────────── */
.sim-debug-panel {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  background: #1a1a2e;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.sim-debug-empty {
  color: #555;
  font-size: 12px;
  text-align: center;
  padding: 24px 16px;
  line-height: 1.6;
}
.sim-dbg-step-header {
  font-size: 10px;
  font-weight: 700;
  color: #4a9eff;
  padding: 8px 4px 2px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}
.sim-dbg-step-header:first-child { padding-top: 2px; }
.sim-dbg-entry {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 4px 6px;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.03);
  font-family: monospace;
  font-size: 11.5px;
  line-height: 1.4;
}
.sim-dbg-entry.dbg-error { background: rgba(220, 38, 38, 0.12); }
.sim-dbg-badge {
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-top: 1px;
}
.badge-node  { background: #1e3a5f; color: #60a5fa; }
.badge-api   { background: #1a3a2e; color: #34d399; }
.badge-error { background: #3a1a1a; color: #f87171; }
.sim-dbg-text { color: #c9d1d9; word-break: break-all; }

.sim-session-var {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 5px 8px;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.04);
  font-family: monospace;
  font-size: 11.5px;
}
.sim-session-key {
  flex-shrink: 0;
  color: #f0883e;
  width: 130px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sim-session-val { color: #9ecbff; word-break: break-all; flex: 1; }
</style>
